import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './hooks/useTheme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import api from './utils/api';
import LoginPage from './components/LoginPage';
import DashboardPage from './components/DashboardPage';
import './styles/theme.css';

// Load Poppins font
if (typeof document !== 'undefined' && !document.getElementById('inter-font')) {
    const link = document.createElement('link');
    link.id = 'inter-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
}

// Load reCAPTCHA script
const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

if (typeof document !== 'undefined') {
    if (RECAPTCHA_KEY && !document.getElementById('recaptcha-script')) {
        const s = document.createElement('script');
        s.id = 'recaptcha-script'; s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`; s.async = true; s.defer = true;
        document.head.appendChild(s);
    }
}

function ProtectedRoute({ children }) {
    const { isAuth, isChatter, user } = useAuth();
    if (!isAuth) return <Navigate to="/login" />;
    if (isChatter && user?.onboarding_status === 'pending') return <Navigate to="/login" />;
    return children;
}

function SandboxBanner() {
    if (typeof window !== 'undefined' && !window.location.hostname.includes('sandbox')) return null;
    return (
        <div style={{
            background: '#f59e0b',
            color: '#000',
            textAlign: 'center',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: 'Poppins, sans-serif',
            letterSpacing: '0.5px',
            position: 'sticky',
            top: 0,
            zIndex: 99999,
        }}>
            SANDBOX — This is a staging environment
        </div>
    );
}

function MaintenanceScreen({ message }) {
    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f5f5f7', fontFamily: 'Poppins, sans-serif', padding: 24,
        }}>
            <div style={{
                background: '#fff', borderRadius: 16, padding: '48px 32px', textAlign: 'center',
                maxWidth: 420, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;&#65039;</div>
                <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>Under Maintenance</h1>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{message}</p>
            </div>
        </div>
    );
}

export default function App() {
    const [maintenance, setMaintenance] = useState(null);

    useEffect(() => {
        const handler = (e) => setMaintenance(e.detail);
        window.addEventListener('maintenance', handler);
        // Proactive check — triggers 503 interceptor if maintenance is active
        api.get('/api/me').catch(() => {});
        return () => window.removeEventListener('maintenance', handler);
    }, []);

    if (maintenance) return <MaintenanceScreen message={maintenance} />;

    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <ToastProvider>
                        <SandboxBanner />
                        <Routes>
                            <Route path="/" element={<Navigate to="/login" replace />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signup" element={<Navigate to="/login?signup=true" replace />} />
                            <Route path="/chatter" element={<Navigate to="/login" replace />} />
                            <Route path="/connect" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/channel/:channelId/wheel" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/channel/:channelId/dms" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/channel/:channelId/vault" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/channel/:channelId/subs" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/payments" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/chatters" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/marketplace" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/settings" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/guide" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dashboard/referrals" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                            <Route path="/dms" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/admin" element={<Navigate to="/login" replace />} />
                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </Routes>
                    </ToastProvider>
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}